/* app/seo-performance.jsx - Performance · Google Search Console, wired to /api/gsc/*
   (status gate → connect/property/sync → summary + series + query/page tables
   + opportunities + sitemaps).

   TWO POSITIONS, AND THEY SHOW DIFFERENT THINGS ON PURPOSE. docs/MODULE-SCOPE.md
   3.1 and 4.6, and PROPERTY-MODEL 3.12c. Google reports a DOMAIN, so this is one
   module at property scope with a section filter, and `sectionId` is that filter:

     at All (sectionId null)   the domain's clicks, impressions, CTR and position,
                               the DIMENSIONLESS daily series Google attributes to
                               no URL, the split of the page rows per section, and
                               a Section column on the Top pages table.
     at a section (a number)   that section's ATTRIBUTED PAGE figures, the
                               website's headline shown beside them as the
                               website's, and the sentence saying a section sits
                               below its share of that headline. The daily series
                               is not shown here, because it cannot be split.

   NOTHING IS RE-DERIVED HERE, which is the rule client/src/app/coverage-map.jsx
   already writes out for page gaps. Which section serves a page, whether the
   headline came from the series or from the page breakdown, how far the sections
   fall short of it and the sentence explaining each of those are all decided by
   the endpoint and rendered verbatim. A second opinion in the client is how one
   screen comes to disagree with another about what a website is.

   THE DAILY TREND IS NO LONGER DEFERRED. This file's header said for a long time
   that the chart had no time-series endpoint, and the TrendChart component sat
   here finished and unused because of it. GET /gsc/series answers it now, one
   point per day at the property, and the chart is wired to that.

   THE SCOPE FORM IS STILL PER CONNECTION, and that is not an oversight. The two
   RE2 expressions bound what ONE connection's nightly sync pulls, so they are
   configuration on the anchor connection and the form keeps writing there. The
   form says so, because a property-wide reading of it would be wrong. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  const SeoStat = (p) => React.createElement(window.MlSeoDashboard.SeoStat, p);
  const fmtN = (n) => (n == null ? "-" : n >= 10000 ? (n / 1000).toFixed(0) + "K" : n >= 1000 ? (n / 1000).toFixed(1) + "K" : String(Math.round(n)));
  const pctOf = (c) => (c == null ? "-" : (c <= 1 ? (c * 100).toFixed(1) : c.toFixed(1)) + "%");
  const pos1 = (p) => (p == null ? "-" : Number(p).toFixed(1));
  const OPP_LABEL = { striking_distance: "Striking distance", low_ctr: "Low CTR", content_gap: "Content gap", decay: "Decaying" };

  const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  // A day label built from the ISO string rather than from a Date, because
  // `new Date("2026-10-18")` is parsed as UTC midnight and then rendered in the
  // viewer's own zone, which moves every label back a day for anybody west of
  // Greenwich. The series is a calendar of Search Console days, not a set of
  // instants, so the string is the truth and parsing it is the risk.
  const dayLabel = (iso) => {
    const p = String(iso || "").split("-");
    return p.length < 3 ? String(iso || "") : MONTH[Number(p[1]) - 1] + " " + Number(p[2]);
  };

  function GoogleMark() {
    return <svg className="seo-g" viewBox="0 0 24 24" width="16" height="16"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>;
  }

  // ---- Trend chart ----
  // Wired to GET /gsc/series. `data` is [{ label, clicks, impr }] and the caller
  // only renders this with at least one point, because the geometry below has no
  // answer for an empty window.
  function TrendChart({ data, show }) {
    const [hover, setHover] = useState(null);
    const W = 760, H = 200, padL = 8, padR = 8, padT = 14, padB = 24;
    const iw = W - padL - padR, ih = H - padT - padB;
    // Floored at 1 rather than taken raw. A real window can carry impressions
    // and no clicks at all, and dividing by a zero maximum put NaN into every
    // path coordinate, which renders as nothing and reads as a broken chart
    // instead of as a flat line along the bottom.
    const maxC = Math.max(1, ...data.map(d => d.clicks)), maxI = Math.max(1, ...data.map(d => d.impr));
    const xs = i => padL + (data.length === 1 ? iw / 2 : (i / (data.length - 1)) * iw);
    const yc = v => padT + ih - (v / maxC) * ih;
    const yi = v => padT + ih - (v / maxI) * ih;
    const lineC = data.map((d, i) => (i ? "L" : "M") + xs(i).toFixed(1) + " " + yc(d.clicks).toFixed(1)).join(" ");
    const lineI = data.map((d, i) => (i ? "L" : "M") + xs(i).toFixed(1) + " " + yi(d.impr).toFixed(1)).join(" ");
    const areaC = lineC + ` L${xs(data.length - 1)} ${padT + ih} L${padL} ${padT + ih} Z`;
    const ticks = data.filter((_, i) => i % Math.ceil(data.length / 6) === 0 || i === data.length - 1);
    return (
      <div className="seo-chart-wrap" onMouseLeave={() => setHover(null)}>
        <svg className="seo-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
             onMouseMove={e => { const r = e.currentTarget.getBoundingClientRect(); const x = (e.clientX - r.left) / r.width * W; const i = Math.round((x - padL) / iw * (data.length - 1)); if (i >= 0 && i < data.length) setHover(i); }}>
          {[0, .25, .5, .75, 1].map(g => <line key={g} className="grid" x1={padL} x2={W - padR} y1={padT + g * ih} y2={padT + g * ih} />)}
          {show.impr && <path className="line-i" d={lineI} />}
          {show.clicks && <path className="area-c" d={areaC} />}
          {show.clicks && <path className="line-c" d={lineC} />}
          {hover != null && <line className="grid" style={{ stroke: "var(--text-disabled)" }} x1={xs(hover)} x2={xs(hover)} y1={padT} y2={padT + ih} />}
          {hover != null && show.clicks && <circle className="dot" cx={xs(hover)} cy={yc(data[hover].clicks)} r="4" />}
          {ticks.map((d, i) => <text key={i} className="axis" x={xs(data.indexOf(d))} y={H - 6} textAnchor="middle">{d.label}</text>)}
        </svg>
        {hover != null && (
          <div className="seo-chart-tip" style={{ left: (xs(hover) / W * 100) + "%", top: 8 }}>
            <div>{data[hover].label}</div>
            {show.clicks && <div><b>{data[hover].clicks.toLocaleString()}</b> clicks</div>}
            {show.impr && <div><b>{data[hover].impr.toLocaleString()}</b> impr.</div>}
          </div>
        )}
      </div>
    );
  }

  function Performance({ site, sectionId, sections }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    // The two pieces of state and they are not the same thing (main.jsx 5):
    // `siteId` is the ANCHOR CONNECTION, sent on every request so the server can
    // authorise it and derive the website from it, and `sectionId` is the FILTER,
    // null at All and otherwise one section of that website.
    const atAll = sectionId == null;
    const secParam = atAll ? "" : `&section_id=${sectionId}`;
    const sectionList = sections || [];
    const thisSection = atAll ? null
      : (sectionList.find(s => s.id === sectionId || s.site_id === sectionId) || null);
    const sectionLabel = thisSection ? thisSection.name : (atAll ? "the whole website" : "this section");

    const [phase, setPhase] = useState("loading"); // loading|oauth-ready|oauth-missing|no-property|no-data|connected|error
    const [status, setStatus] = useState(null);
    const [props, setProps] = useState([]);
    const [propInput, setPropInput] = useState("");
    const [tab, setTab] = useState("queries");
    const [range, setRange] = useState("28d");
    const [summary, setSummary] = useState(null);
    const [series, setSeries] = useState(null);
    const [show, setShow] = useState({ clicks: true, impr: true });
    const [queries, setQueries] = useState([]);
    const [queryMeta, setQueryMeta] = useState(null);
    const [pages, setPages] = useState([]);
    const [pageMeta, setPageMeta] = useState(null);
    const [opps, setOpps] = useState([]);
    const [oppMeta, setOppMeta] = useState(null);
    const [sitemaps, setSitemaps] = useState([]);
    const [syncing, setSyncing] = useState(false);
    const [actionErr, setActionErr] = useState("");
    const [scopeOpen, setScopeOpen] = useState(false);
    const [scopeInput, setScopeInput] = useState("");
    // The other half of the scope. A site on a shared domain often cannot be
    // described by inclusion alone: Search Console matches with RE2, which has
    // no negative lookahead, so "this domain except its blog" cannot be written
    // as a page filter at all and has to be expressed as an exclusion.
    const [excludeInput, setExcludeInput] = useState("");

    // THE GATE ASKS ABOUT THE WEBSITE, NOT ABOUT THE SECTION, and that is
    // deliberate: no `section_id` is sent here while every other call carries it.
    //
    // /gsc/status counts the stored rows through whatever filter it was given,
    // so asked at a section it counts THAT CONNECTION's rows. A section can have
    // real figures and no rows of its own: attribution is by longest matching
    // declared publish address over the whole property's rows, so the crawl-only
    // front end on the live property is attributed 70 pages' worth of traffic
    // that another connection pulled. Gated on its own row count it would be
    // sent to "pull your history" for a section that already has figures on
    // screen, which is the same class of bug the server fixed for `property`
    // when it let one section inherit the mapping another declared. "Has this
    // website ever been synced" is the question the gate is actually asking.
    const loadStatus = useCallback(async () => {
      if (siteId == null) { setPhase("error"); return; }
      setPhase("loading");
      try {
        const s = await api(`/gsc/status?site_id=${siteId}`); setStatus(s); setScopeInput(s.pageFilter || ""); setExcludeInput(s.pageExclude || "");
        if (!s.configured) { if (s.oauthAvailable) setPhase("oauth-ready"); else setPhase("oauth-missing"); return; }
        // `property` is already the inherited answer when another section of
        // this website declared one, so this never sends a customer to pick a
        // property for a website that has one. Which section declared it is
        // named on screen below, because inheriting is not owning.
        if (!s.property) { const sl = await api(`/gsc/sites`).catch(() => ({ sites: [] })); setProps((sl && sl.sites) || []); setPhase("no-property"); return; }
        setPhase(s.rows ? "connected" : "no-data");
      } catch { setPhase("error"); }
    }, [siteId]);
    useEffect(() => { loadStatus(); }, [loadStatus]);

    const loadData = useCallback(async () => {
      if (siteId == null) return;
      const base = `site_id=${siteId}${secParam}`;
      try {
        const [sm, q, p, op, sm2, se] = await Promise.all([
          api(`/gsc/summary?${base}&range=${range}`).catch(() => null),
          api(`/gsc/performance?${base}&dimension=query&range=${range}&limit=25`).catch(() => ({ rows: [] })),
          api(`/gsc/performance?${base}&dimension=page&range=${range}&limit=25`).catch(() => ({ rows: [] })),
          api(`/gsc/opportunities?${base}&range=${range}`).catch(() => ({ opportunities: [] })),
          // Sitemaps stay per connection: the endpoint is a live Search Console
          // call through THIS connection's own mapped property, so it has no
          // property-wide answer to give and is not asked for one.
          api(`/gsc/sitemaps?site_id=${siteId}`).catch(() => ({ sitemaps: [] })),
          // Not fetched at a section at all. The series cannot be split, the
          // screen does not show it there, and asking for it would be asking
          // the server for a figure with nowhere to go.
          atAll ? api(`/gsc/series?site_id=${siteId}&range=${range}`).catch(() => null) : Promise.resolve(null),
        ]);
        setSummary(sm);
        setQueries((q && q.rows) || []); setQueryMeta(q || null);
        setPages((p && p.rows) || []); setPageMeta(p || null);
        setOpps((op && op.opportunities) || []); setOppMeta(op || null);
        setSitemaps((sm2 && sm2.sitemaps) || []);
        setSeries(se);
      } catch { /* every call above falls back on its own */ }
    }, [siteId, range, secParam, atAll]);
    useEffect(() => { if (phase === "connected") loadData(); }, [phase, loadData]);
    // The Connections tab exists only at All, because it is the whole website's
    // connection state. Moving the filter onto a section while it is open would
    // otherwise leave a selected tab with nothing to render.
    useEffect(() => { if (!atAll && tab === "connections") setTab("queries"); }, [atAll, tab]);

    const connect = () => { window.location.href = "/api/gsc/oauth/start"; };
    const sync = async (backfillDays) => { if (siteId == null) return; const bf = Number.isFinite(backfillDays) ? `&backfill=${backfillDays}` : ""; setSyncing(true); setActionErr(""); try { await api(`/gsc/sync?site_id=${siteId}${bf}`, { method: "POST", body: "{}" }); await loadStatus(); } catch (e) { setActionErr(e.message || "Sync failed."); } finally { setSyncing(false); } };
    const saveScope = async () => {
      if (siteId == null) return;
      setActionErr("");
      try {
        await api(`/gsc/property?site_id=${siteId}`, { method: "POST", body: JSON.stringify({ page_filter: scopeInput, page_exclude: excludeInput }) });
        setScopeOpen(false);
        await loadStatus();
      } catch (e) { setActionErr(e.message || "Could not save the scope."); }
    };
    // Editing the scope is a small FORM, not a toolbar. Two regular
    // expressions with their own labels do not belong on one line beside the
    // buttons: squeezed in there both inputs ended up narrow and set in tiny
    // monospace, which is unreadable for the one kind of value where every
    // character matters. Open, it becomes a stacked panel; closed, it stays
    // the single summary line it always was.
    //
    // AND IT IS ONE CONNECTION'S SETTING. The pair bounds what the nightly sync
    // pulls for the anchor connection, so it writes where it always wrote and
    // the intro says whose setting it is. Read as a property-wide control it
    // would look like it governed figures it has nothing to do with: the reads
    // on this screen are at the website and attribute pages by declared publish
    // address, with no regex involved anywhere.
    const renderScope = (repull = false) => (
      <div className={"seo-scope" + (scopeOpen ? " seo-scope--editing" : "")}>
        {scopeOpen ? (
          <div className="seo-scope__form">
            <div className="seo-scope__intro">
              One Search Console property can cover a whole domain, so tell MarketLens which of its pages belong to this site. Both boxes take a regular expression matched against the page address, and you will usually need only one of them.
              {" "}This scope belongs to <b>{(site && site.name) || "this connection"}</b> alone: it bounds what that one connection pulls from Google, and every other section of this website declares its own.
            </div>
            <label className="seo-scope__field">
              <span className="seo-scope__fieldlabel">Count only pages matching</span>
              <input className="hds-input seo-scope__input" value={scopeInput} placeholder="^https://www\.example\.com/blog" onChange={e => setScopeInput(e.target.value)} />
              <span className="seo-scope__hint">Leave empty to start from every page on the property. Begin with <code>^</code> and the full address so a pattern like <code>/blog</code> cannot also match <code>/en/blog</code>.</span>
            </label>
            <label className="seo-scope__field">
              <span className="seo-scope__fieldlabel">Leave out pages matching</span>
              <input className="hds-input seo-scope__input" value={excludeInput} placeholder="/blog" onChange={e => setExcludeInput(e.target.value)} />
              <span className="seo-scope__hint">Use this for "everything except". Google's matching cannot express "not this" inside a pattern, which is why it has its own box.</span>
            </label>
            <div className="seo-scope__actions">
              <button className="bi-btn bi-btn--primary" onClick={saveScope}>Save scope</button>
              <button className="bi-btn" onClick={() => { setScopeInput((status && status.pageFilter) || ""); setExcludeInput((status && status.pageExclude) || ""); setScopeOpen(false); }}>Cancel</button>
              <span className="seo-scope__warn">Saving clears the figures stored for this site: they were counted under the old scope. Pull your history again afterwards.</span>
            </div>
          </div>
        ) : (
          <>
            <span className="seo-scope__label">
              {status && (status.pageFilter || status.pageExclude)
                ? <>
                    Counting {status.pageFilter ? <>pages matching <code>{status.pageFilter}</code></> : <>every page</>}
                    {status.pageExclude ? <>, except <code>{status.pageExclude}</code></> : null}
                  </>
                : <>Counting every page on {status && status.property ? status.property : "the property"}</>}
              {site ? <>, for {site.name}</> : null}
            </span>
            <button className="bi-btn bi-btn--sm" onClick={() => setScopeOpen(true)}><I name="filter" size={14} />Change scope</button>
            {repull && <button className="bi-btn bi-btn--sm" disabled={syncing} onClick={() => sync(90)}>{syncing ? "Pulling…" : <><I name="download" size={14} />Re-pull 90 days</>}</button>}
          </>
        )}
      </div>
    );
    const useProperty = async (property) => { if (!property || siteId == null) return; setActionErr(""); try { await api(`/gsc/property?site_id=${siteId}`, { method: "POST", body: JSON.stringify({ property }) }); await loadStatus(); } catch (e) { setActionErr(e.message || "Could not save the property."); } };

    // Which section declared the mapping this screen reads through, when it was
    // not this connection. Never null and never false at once: the server sends
    // `property_from_section` only when `property` was inherited.
    const inherited = status && status.property_from_section;
    const renderInherited = () => (inherited ? (
      <div className="bi-hint" style={{ marginBottom: 16 }}>
        The Search Console property <b>{status.property}</b> was mapped by <b>{inherited.name}</b>, another section of this website, and this screen reads through it. {(site && site.name) || "This connection"} has declared none of its own, so the scope below is still its own setting and not that section's.
      </div>
    ) : null);

    const scopeLabel = atAll ? "the whole website" : sectionLabel;

    if (phase === "loading") return <div className="bi-page"><PerfHeader scopeLabel={scopeLabel} /><div className="bi-subempty">Checking Search Console…</div></div>;
    if (phase === "error") return <div className="bi-page"><PerfHeader scopeLabel={scopeLabel} /><div className="bi-subempty">Couldn't reach Search Console. <a className="bi-viewdetails" style={{ display: "inline-flex" }} href="#" onClick={e => { e.preventDefault(); loadStatus(); }}>Recheck</a></div></div>;

    if (phase === "oauth-ready") return (
      <div className="bi-page"><PerfHeader scopeLabel={scopeLabel} /><div className="seo-connect"><div className="seo-connect-card">
        <span className="seo-connect-card__icon"><I name="trending-up" size={28} /></span>
        <div className="seo-connect-card__title">Connect Search Console</div>
        <p className="seo-connect-card__desc">See clicks, impressions, CTR and position{site ? " for " + site.name : ""}, straight from Google.</p>
        <div className="seo-connect-card__actions"><button className="bi-btn seo-google-btn" onClick={connect}><GoogleMark />Connect with Google</button><button className="bi-btn" onClick={loadStatus}><I name="refresh" size={16} />Recheck</button></div>
        <div className="seo-connect-tip">You'll be sent to Google's consent screen to authorize read-only Search Console access.</div>
      </div></div></div>
    );
    if (phase === "oauth-missing") return (
      <div className="bi-page"><PerfHeader scopeLabel={scopeLabel} /><div className="seo-connect"><div className="seo-connect-card" style={{ textAlign: "left" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 6 }}><span className="seo-connect-card__icon" style={{ margin: 0, width: 44, height: 44 }}><I name="key" size={22} /></span><div className="seo-connect-card__title" style={{ fontSize: "var(--text-lg)" }}>Finish OAuth setup</div></div>
        <p className="seo-connect-card__desc" style={{ textAlign: "left" }}>Search Console isn't connectable yet. On the server, set <span className="seo-code">GSC_OAUTH_CLIENT_ID</span> and <span className="seo-code">GSC_OAUTH_CLIENT_SECRET</span> (a Google Cloud OAuth client with redirect URI <span className="seo-code">/api/gsc/oauth/callback</span>), enable the Search Console API, then recheck.</p>
        <div className="seo-connect-card__actions" style={{ justifyContent: "flex-start" }}><button className="bi-btn" onClick={loadStatus}><I name="refresh" size={16} />Recheck connection</button></div>
      </div></div></div>
    );
    if (phase === "no-property") {
      // Reached only when NO section of this website has a mapping, because the
      // server hands back an inherited one when any of them does. Said out loud
      // when the website has more than one piece, so this does not read as
      // "pick one for this section" when it means "this website has none".
      const mapped = ((status && status.sections) || []).length;
      return (
        <div className="bi-page"><PerfHeader scopeLabel={scopeLabel} /><div className="seo-connect" style={{ maxWidth: 600 }}>
          <div className="seo-connect-card" style={{ textAlign: "left" }}>
            <div className="seo-connect-card__title" style={{ fontSize: "var(--text-lg)" }}>Pick a Search Console property</div>
          {actionErr && <div className="ds-strip ds-strip--error"><I name="alert-triangle" size={14} /><span>{actionErr}</span></div>}
            <p className="seo-connect-card__desc" style={{ textAlign: "left", marginBottom: 16 }}>Choose which verified property maps to{site ? " " + site.name : " this site"}.</p>
            {mapped > 1 && (
              <div className="bi-hint" style={{ marginBottom: 16 }}>
                None of this website's {mapped} sections has a Search Console property mapped yet, so there is nothing for this one to inherit. Mapping one here maps it for {(site && site.name) || "this connection"}, and the rest of the website can read through it.
              </div>
            )}
            <div className="seo-prop-list">
              {props.length === 0 && <div className="bi-subempty">No verified properties found on this Google account.</div>}
              {props.map(p => (
                <div className="seo-prop" key={p.siteUrl}><span className="seo-prop__icon"><I name="globe" size={16} /></span><span className="seo-prop__meta"><div className="seo-prop__url">{p.siteUrl}</div><div className="seo-prop__type">{p.permissionLevel || "-"}</div></span><button className="bi-btn bi-btn--sm bi-btn--primary" onClick={() => useProperty(p.siteUrl)}>Use this</button></div>
              ))}
            </div>
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border-subtle)" }}>
              <label className="bi-field__label">Or paste a property URL</label>
              <div style={{ display: "flex", gap: 8 }}><input className="hds-input" value={propInput} onChange={e => setPropInput(e.target.value)} placeholder="sc-domain:example.com" /><button className="bi-btn" onClick={() => useProperty(propInput.trim())}>Save</button></div>
            </div>
          </div>
        </div></div>
      );
    }
    if (phase === "no-data") return (
      <div className="bi-page"><PerfHeader scopeLabel={scopeLabel} /><div className="seo-connect"><div className="seo-connect-card">
        <span className="seo-connect-card__icon"><I name="download" size={28} /></span>
        <div className="seo-connect-card__title">No performance data yet</div>
        {actionErr && <div className="ds-strip ds-strip--error"><I name="alert-triangle" size={14} /><span>{actionErr}</span></div>}
        <p className="seo-connect-card__desc">Property mapped{status && status.property ? " to " + status.property : ""}. Pull your history to get started.</p>
        {renderInherited()}
        {renderScope()}
        <div className="seo-connect-card__actions"><button className="bi-btn bi-btn--primary" disabled={syncing} onClick={() => sync(90)}>{syncing ? <><span className="ml-spinner ml-spinner--sm ml-spinner--on-primary" style={{ width: 14, height: 14 }} />Syncing…</> : <><I name="download" size={16} />Sync history</>}</button></div>
        <div className="seo-connect-tip">Search Console data has a 2-3 day lag, so the most recent days may be incomplete.</div>
      </div></div></div>
    );

    // ---- connected ----
    const cur = (summary && summary.current) || {}, prev = (summary && summary.previous) || {};
    // At All: which series answered the four numbers, and why. At a section:
    // the website's headline, reported BESIDE the section's figures and never as
    // them, because it cannot be split.
    const head = (summary && summary.headline) || null;
    const propHead = (summary && summary.property_headline) || null;
    const below = (summary && summary.below) || null;
    const split = (summary && summary.sections) || [];
    const noSection = (summary && summary.no_section) || null;
    const delta = (c, p, lowerBetter) => {
      if (c == null || p == null || !p) return undefined;
      const up = c >= p, good = lowerBetter ? !up : up;
      return { dir: good ? "up" : "down", value: Math.abs(Math.round((c - p) / p * 100)) + "%", note: "vs prev" };
    };
    const chart = ((series && series.points) || []).map(p => ({ label: dayLabel(p.date), clicks: p.clicks, impr: p.impressions }));
    // THE COUNT COMES FROM LIVE STATE, NOT FROM THE LIST. /gsc/performance sends
    // `count`, how many attributed page rows there are, and `rows` capped at the
    // limit asked for. Counting the rows would report 25 for a section with 300.
    const pageCount = pageMeta && pageMeta.count != null ? pageMeta.count : pages.length;
    const oppCount = oppMeta && oppMeta.total != null ? oppMeta.total : opps.length;
    const OPP_CARDS = opps.map(o => ({ sev: o.score >= 70 ? "high" : "medium", type: OPP_LABEL[o.type] || o.type, metric: `${fmtN(o.impressions)} impr · ${pctOf(o.ctr)} CTR · #${pos1(o.position)}`, desc: (o.plan && (o.plan.title || o.plan)) || o.key }));
    const setup = pageMeta && pageMeta.scope && pageMeta.scope.setup;
    const tabs = [["queries", "Top queries", queries.length], ["pages", "Top pages", pageCount], ["opps", "Opportunities", oppCount], ["sitemaps", "Sitemaps", sitemaps.length]];
    if (atAll) tabs.push(["connections", "Connections", ((status && status.sections) || []).length]);
    // Both halves of the label, because "no section serves this page" and
    // "nothing has been declared yet" are different facts, and a cell that shows
    // only one of them puts back the conflation the data refused.
    const sectionCell = (r) => (r.section_name ? r.section_name
      : r.belongs_to_no_section ? "No section"
      : r.section_not_derivable ? "Not derivable" : "-");

    return (
      <div className="bi-page">
        <PerfHeader site={site} scopeLabel={scopeLabel} range={range} setRange={setRange} syncing={syncing} onRefresh={() => sync()} />
        {actionErr && <div className="ds-strip ds-strip--error"><I name="alert-triangle" size={14} /><span>{actionErr}</span></div>}
        {renderInherited()}
        {renderScope(true)}
        <div className="seo-stat-row">
          <SeoStat icon="cursor" label="Clicks" value={fmtN(cur.clicks)} delta={delta(cur.clicks, prev.clicks)} />
          <SeoStat icon="eye" label="Impressions" value={fmtN(cur.impressions)} delta={delta(cur.impressions, prev.impressions)} />
          <SeoStat icon="target" label="Avg. CTR" value={pctOf(cur.ctr)} delta={delta(cur.ctr, prev.ctr)} />
          <SeoStat icon="trending-up" label="Avg. position" value={pos1(cur.position)} delta={delta(cur.position, prev.position, true)} />
        </div>

        {/* WHERE THE FOUR NUMBERS CAME FROM, on screen rather than in a document.
            A figure whose source is invisible is a figure nobody can check, and
            at All these four come from one of two different measurements of the
            same window: the dimensionless daily series, or the page breakdown
            summed. `from_why` is the endpoint's own sentence for the one it
            picked and is rendered as it stands. */}
        {atAll && head && (
          <div className="bi-hint" style={{ marginBottom: 16 }}>
            <b>These four numbers are the whole website's, read from {head.from === "total_series" ? "the dimensionless daily series" : "the page breakdown"}.</b>{" "}
            {head.from_why}
            {head.contributors ? <> Measured across {head.contributors} connection{head.contributors === 1 ? "" : "s"} of this website, with the series covering {head.series_days} day(s) of the window and the breakdown {head.page_days}.</> : null}
          </div>
        )}

        {/* AT A SECTION THIS SENTENCE IS REQUIRED, not decorative
            (MODULE-SCOPE 3.12c limit 2). The four numbers above are the pages
            Google named for this section, and they sit BELOW its real share of
            the website's traffic, always, because Google drops impressions it
            cannot attribute to a URL. `below.why` is built from the figures by
            the endpoint, so it can never claim a gap that is not there, and it
            sits directly under the cards it is about rather than behind a tab. */}
        {!atAll && (
          <div className="bi-hint" style={{ marginBottom: 16 }}>
            <b>These four numbers are the pages Google attributed to {sectionLabel}.</b>{" "}
            {below ? below.why : null}
          </div>
        )}
        {!atAll && propHead && (
          <div className="ds-strip ds-strip--quiet">
            <I name="globe" size={14} />
            <span>
              The whole website over the same window: {fmtN(propHead.clicks)} clicks, {fmtN(propHead.impressions)} impressions, {pctOf(propHead.ctr)} CTR, position {pos1(propHead.position)}. That headline is the website's and not this section's, and it is shown here only so the figures above can be read against it.
            </span>
          </div>
        )}
        {!atAll && (
          <div className="ds-strip ds-strip--quiet">
            <I name="trending-up" size={14} />
            <span>
              The daily trend is not shown at a section. Search Console attributes those rows to no URL, so there is no address to match against a section's publish address and no honest way to split the series. Put the filter on All to see the website's.
            </span>
          </div>
        )}

        {/* The chart the file's header note deferred for want of an endpoint.
            PROPERTY ONLY, one point per day. */}
        {atAll && chart.length > 0 && (
          <div className="seo-chart-card">
            <div className="seo-chart-head">
              <div className="ml-seohead__title" style={{ fontSize: "var(--text-md)" }}>Clicks and impressions per day</div>
              <div className="seo-chart-legend">
                <button className={show.clicks ? "on" : ""} onClick={() => setShow(s => ({ ...s, clicks: !s.clicks }))}><i style={{ background: "var(--primary)" }} />Clicks</button>
                <button className={show.impr ? "on" : ""} onClick={() => setShow(s => ({ ...s, impr: !s.impr }))}><i style={{ background: "var(--blue-500)" }} />Impressions</button>
              </div>
            </div>
            <TrendChart data={chart} show={show} />
            {series && series.dedup_why && (
              <div className="bi-hint" style={{ marginTop: 14 }}>
                <b>{chart.length} day(s) of the window carry a measurement.</b> {series.dedup_why}
              </div>
            )}
          </div>
        )}
        {atAll && series && chart.length === 0 && (
          <div className="ds-strip ds-strip--quiet">
            <I name="trending-up" size={14} />
            <span>No dimensionless daily rows are stored for this window, so there is no trend to draw. The four numbers above came from the page breakdown instead, which is what the note beside them says.</span>
          </div>
        )}

        {/* THE SPLIT, AND THE GAP BETWEEN IT AND THE HEADLINE. The sections add
            up to LESS than the headline by construction, so the shortfall is
            stated rather than smoothed over: `below` carries the clicks and
            impressions Google attached to no URL at all, and the sentence under
            the table is the endpoint's. */}
        {atAll && summary && (
          <>
            <div className="bi-results-head"><I name="layers" size={14} />Per section, over the same window</div>
            <div className="kw-table-wrap" style={{ marginBottom: 12 }}>
              <table className="kw-table">
                <thead><tr><th>Section</th><th>Pages</th><th>Clicks</th><th>Impr.</th><th>CTR</th><th>Pos.</th></tr></thead>
                <tbody>
                  {split.map(s => (
                    <tr key={s.section_id}>
                      <td><span className="kw-kw">{s.name}</span>{s.can_publish === false ? <span className="seo-prop__type"> no CMS behind it</span> : null}</td>
                      <td className="kw-num">{fmtN(s.pages)}</td>
                      <td className="kw-num">{fmtN(s.clicks)}</td>
                      <td className="kw-num">{fmtN(s.impressions)}</td>
                      <td className="kw-num">{pctOf(s.ctr)}</td>
                      <td className="kw-num">{pos1(s.position)}</td>
                    </tr>
                  ))}
                  {noSection && (
                    <tr>
                      <td><span className="kw-kw">No section</span><span className="seo-prop__type"> no declared publish address reaches these pages</span></td>
                      <td className="kw-num">{fmtN(noSection.pages)}</td>
                      <td className="kw-num">{fmtN(noSection.clicks)}</td>
                      <td className="kw-num">{fmtN(noSection.impressions)}</td>
                      <td className="kw-num">{pctOf(noSection.ctr)}</td>
                      <td className="kw-num">{pos1(noSection.position)}</td>
                    </tr>
                  )}
                  {split.length === 0 && !noSection && <tr><td colSpan={6}><span className="seo-prop__type">No page rows in this window.</span></td></tr>}
                </tbody>
              </table>
            </div>
            {below && (
              <div className="bi-hint" style={{ marginBottom: 16 }}>
                <b>The rows above add up to less than the headline.</b> {below.why}
              </div>
            )}
            {summary.tied_why && (
              <div className="ds-strip ds-strip--quiet"><I name="alert-triangle" size={14} /><span>{summary.tied_why}</span></div>
            )}
          </>
        )}

        <div className="kw-seg" style={{ marginBottom: 16 }}>
          {tabs.map(([id, l, n]) => (
            <button key={id} className={tab === id ? "on" : ""} onClick={() => setTab(id)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {id === "opps" && <I name="sparkles" size={13} />}{id === "sitemaps" && <I name="layers" size={13} />}{id === "connections" && <I name="globe" size={13} />}{l}{n != null && <span className="tb-tab__count" style={{ background: tab === id ? "var(--primary-tint)" : "var(--gray-100)" }}>{n}</span>}
            </button>
          ))}
        </div>

        {tab === "queries" && (
          <>
            {/* WHY THIS TABLE DIFFERS FROM THE ONE NEXT TO IT, said on screen.
                A page is keyed to a URL and can be attributed to a section; a
                query is keyed to nothing, so at a section these are the rows
                that connection's own Search Console scope pulled. Without this
                sentence the two tables look like two views of one question. */}
            {queryMeta && queryMeta.attributed_why && (
              <div className="bi-hint" style={{ marginBottom: 12 }}>
                <b>{atAll ? "Queries over the whole website." : "Pulled by this section's own Search Console scope."}</b> {queryMeta.attributed_why}
              </div>
            )}
            <div className="kw-table-wrap">
              <table className="kw-table">
                <thead><tr><th>Query</th><th>Clicks</th><th>Impr.</th><th>CTR</th><th>Pos.</th></tr></thead>
                <tbody>{queries.map((r, i) => (
                  <tr key={i}><td><span className="kw-kw">{r.key}</span></td><td className="kw-num">{fmtN(r.clicks)}</td><td className="kw-num">{fmtN(r.impressions)}</td><td className="kw-num">{pctOf(r.ctr)}</td><td className="kw-num">{pos1(r.position)}</td></tr>
                ))}</tbody>
              </table>
              {queries.length === 0 && <div className="bi-subempty">No rows for this range.</div>}
            </div>
          </>
        )}

        {tab === "pages" && (
          <>
            {pageMeta && pageMeta.attributed_why && (
              <div className="bi-hint" style={{ marginBottom: 12 }}>
                <b>{pageCount} page(s) carry impressions{atAll ? " across this website" : " for " + sectionLabel}{pages.length < pageCount ? `, and the ${pages.length} with the most clicks are listed` : ""}.</b> {pageMeta.attributed_why}
              </div>
            )}
            {setup && setup.public_base_declared === false && setup.public_base_why && (
              <div className="ds-strip ds-strip--quiet"><I name="alert-triangle" size={14} /><span>{setup.public_base_why}</span></div>
            )}
            {pageMeta && pageMeta.capped && pageMeta.cap_why && (
              <div className="ds-strip ds-strip--quiet"><I name="alert-triangle" size={14} /><span>{pageMeta.cap_why}</span></div>
            )}
            {pageMeta && pageMeta.tied_why && (
              <div className="ds-strip ds-strip--quiet"><I name="alert-triangle" size={14} /><span>{pageMeta.tied_why}</span></div>
            )}
            <div className="kw-table-wrap">
              <table className="kw-table">
                {/* The Section column exists at All and comes off at a section,
                    which is MODULE-SCOPE 1.1: a union list where a row does not
                    name its section is unreadable, and once the filter names one
                    the column repeats it on every row. */}
                <thead><tr><th>Page</th>{atAll && <th>Section</th>}<th>Clicks</th><th>Impr.</th><th>CTR</th><th>Pos.</th></tr></thead>
                <tbody>{pages.map((r, i) => (
                  <tr key={i}>
                    <td><span className="kw-kw">{r.key}</span></td>
                    {atAll && <td><span className="seo-prop__type">{sectionCell(r)}{r.tied ? " (claimed twice)" : ""}</span></td>}
                    <td className="kw-num">{fmtN(r.clicks)}</td>
                    <td className="kw-num">{fmtN(r.impressions)}</td>
                    <td className="kw-num">{pctOf(r.ctr)}</td>
                    <td className="kw-num">{pos1(r.position)}</td>
                  </tr>
                ))}</tbody>
              </table>
              {pages.length === 0 && <div className="bi-subempty">No rows for this range.</div>}
            </div>
          </>
        )}

        {tab === "opps" && (
          <>
            {oppMeta && oppMeta.attributed_why && (
              <div className="bi-hint" style={{ marginBottom: 12 }}>
                <b>{atAll ? "Over the whole website." : "Over " + sectionLabel + "."}</b> {oppMeta.attributed_why}
              </div>
            )}
            {OPP_CARDS.length === 0 ? <div className="bi-subempty">No opportunities surfaced for this range.</div> :
            <div className="bi-list">
              {OPP_CARDS.map((o, i) => (
                <div className="bi-card" key={i} style={{ cursor: "default" }}>
                  <div className="seo-opp">
                    <div className="seo-opp__head"><span className={"priority-badge priority-" + o.sev}>{o.sev}</span><span className="seo-opp__type">{o.type}</span></div>
                    <span className="seo-opp__metric"><I name="trending-up" size={12} />{o.metric}</span>
                    <p className="seo-opp__desc">{o.desc}</p>
                  </div>
                </div>
              ))}
            </div>}
          </>
        )}

        {tab === "sitemaps" && (
          <>
            {/* Named because it is the one tab on this screen that is NOT the
                website's: the endpoint is a live call through the anchor
                connection's own mapped property, and it has no property-wide
                answer to give. */}
            <div className="ds-strip ds-strip--quiet">
              <I name="layers" size={14} />
              <span>Read live from {(status && status.property) || "the mapped property"}, through {(site && site.name) || "this connection"}. Sitemaps are reported per Search Console property, so this is one connection's answer and not the whole website's.</span>
            </div>
            {sitemaps.length === 0 ? <div className="bi-subempty">No sitemaps reported.</div> :
            <div className="bi-list">
              {sitemaps.map((s, i) => {
                const { Badge } = window.HuddleDesignSystem_775113;
                const st = (s.errors > 0) ? "warn" : s.isPending ? "pending" : "ok";
                const tone = st === "ok" ? "success" : st === "warn" ? "warning" : "neutral";
                return (
                  <div className="bi-card" key={i} style={{ cursor: "default" }}>
                    <div className="seo-sitemap">
                      <span className="seo-sitemap__icon"><I name="layers" size={16} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}><div className="seo-sitemap__path">{s.path}</div><div className="seo-sitemap__meta">{fmtN(s.indexed)} indexed / {fmtN(s.submitted)} submitted</div></div>
                      <Badge tone={tone} dot>{st === "ok" ? "OK" : st === "warn" ? (s.errors + " errors") : "Pending"}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>}
          </>
        )}

        {/* THE WHOLE WEBSITE'S CONNECTION STATE, AT ALL. Before the filter
            existed this screen could only ever show the one connection it stood
            on, so a customer could not see that the mapping sat on the blog and
            the front end had none. A section with nothing mapped is listed with
            its blank rather than left out: emptiness is a state a screen renders. */}
        {tab === "connections" && (
          <>
            <div className="bi-hint" style={{ marginBottom: 12 }}>
              <b>One row per section of this website.</b> The property and the two expressions are configuration on that section's own connection, and the nightly sync pulls each section under its own. A section with nothing mapped contributes no stored rows, which does not stop its pages being attributed to it: a page belongs to the section whose declared publish address matches it longest, whoever pulled it.
            </div>
            <div className="kw-table-wrap">
              <table className="kw-table">
                <thead><tr><th>Section</th><th>Search Console property</th><th>Count only</th><th>Leave out</th><th>Last sync</th></tr></thead>
                <tbody>{((status && status.sections) || []).map(m => (
                  <tr key={m.section_id}>
                    <td><span className="kw-kw">{m.name}</span><span className="seo-prop__type">{m.section_id === siteId ? " this connection" : ""}{m.source === "crawl" ? " no CMS behind it" : ""}</span></td>
                    <td>{m.property ? <span className="seo-prop__url">{m.property}</span> : <span className="seo-prop__type">none mapped</span>}</td>
                    <td>{m.page_filter ? <span className="seo-prop__url">{m.page_filter}</span> : <span className="seo-prop__type">every page</span>}</td>
                    <td>{m.page_exclude ? <span className="seo-prop__url">{m.page_exclude}</span> : <span className="seo-prop__type">nothing</span>}</td>
                    <td><span className="seo-prop__type">{m.last_sync ? String(m.last_sync).slice(0, 10) : "never"}</span></td>
                  </tr>
                ))}</tbody>
              </table>
              {((status && status.sections) || []).length === 0 && <div className="bi-subempty">This connection is not grouped under a property yet, so there are no sections to list.</div>}
            </div>
          </>
        )}

        <div className="seo-stamp">
          <span>{(status && status.property) || "-"}</span><span className="seo-stamp__dot" />
          <span>{atAll ? "the whole website" : sectionLabel}</span><span className="seo-stamp__dot" />
          <span>data has a 2-3 day lag</span>
        </div>
      </div>
    );
  }

  function PerfHeader({ site, scopeLabel, range, setRange, syncing, onRefresh }) {
    return (
      <div className="bi-header">
        <div className="bi-header__titles">
          <h1 className="bi-title"><span className="bi-title__icon"><I name="trending-up" size={20} /></span>Performance</h1>
          {/* The position is in the subtitle because the two positions show
              different figures, and a customer reading a number has to be able
              to see what it is a number OF without clicking anything. */}
          <div className="bi-subtitle">Google Search Console{site ? " · " + site.name : ""}{scopeLabel ? <> · <b>{scopeLabel}</b></> : null}</div>
        </div>
        {setRange && (
          <div className="bi-header-actions">
            <div className="seo-range">{[["7d", "7 days"], ["28d", "28 days"], ["3m", "3 months"], ["12m", "12 months"]].map(([id, l]) => <button key={id} className={range === id ? "on" : ""} onClick={() => setRange(id)}>{l}</button>)}</div>
            <button className="bi-btn" onClick={onRefresh}>{syncing ? <><span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} />Syncing…</> : <><I name="refresh" size={16} />Refresh</>}</button>
          </div>
        )}
      </div>
    );
  }

  window.MlSeoPerformance = { Performance };
})();
